import {
  Box,
  Paper,
  Button,
  Switch,
  Divider,
  Backdrop,
  Container,
  TextField,
  Typography,
  CircularProgress,
  FormControlLabel,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { renderFormattedDate } from 'utils';
import apiService from 'library/api-service';
import { useParams } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import logoSrc from 'assets/images/idecon-header-logo.png';
import loaderLogoSrc from 'assets/images/idecon-loader-logo.png';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ColorModeIconDropdown, LanguagePicker } from 'components';
import { EffortDetailsForCustomerConfirmation } from 'library/types';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';

export type EffortConfirmationPostParams = {
  uniqueKey: string;
  isApproved: boolean;
  sasNo: string;
  isSasNecessary: boolean;
  invoiceNote: string;
  companyVknForInvoice: string;
};

const VKN_REGEX = /^\d{10}$/;
const isGuid = (v: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v);

const ConfirmEffort = () => {
  // other hooks
  const { enqueueSnackbar } = useSnackbar();
  const { guid } = useParams<{ guid: string }>();

  // useState hooks
  const [sasNo, setSasNo] = useState('');
  const [loading, setLoading] = useState(false);
  const [invoiceNote, setInvoiceNote] = useState('');
  const [isSasNecessary, setIsSasNecessary] = useState(true);
  const [approvalMessage, setApprovalMessage] = useState('');
  const [companyVknForInvoice, setCompanyVknForInvoice] = useState('');
  const [effortDetails, setEfforDetails] = useState<EffortDetailsForCustomerConfirmation | null>(null);

  // dynamic variables
  const rows = useMemo(
    () => [
      {
        labelId: 'confirmEffort.projectSelection',
        defaultLabel: 'Proje Seçimi:',
        value: effortDetails?.bookTitle ?? '',
      },
      {
        labelId: 'confirmEffort.requestingCompany',
        defaultLabel: 'Talep Eden Şirket:',
        value: effortDetails?.companyTitle ?? '',
      },
      {
        labelId: 'confirmEffort.relatedProduct',
        defaultLabel: 'İlgili Ürün:',
        value: effortDetails?.projectTitle ?? '',
      },
      {
        labelId: 'confirmEffort.effortDescription',
        defaultLabel: 'Efor Açıklaması:',
        value: effortDetails?.effortExplanation ?? '',
      },
      { labelId: 'confirmEffort.effortDay', defaultLabel: 'Efor Günü:', value: String(effortDetails?.effortDay ?? '') },
      {
        labelId: 'confirmEffort.estimatedStartDate',
        defaultLabel: 'Tahmini Başlangıç:',
        value: effortDetails?.estimatedStartDate ? renderFormattedDate(effortDetails.estimatedStartDate) : '',
      },
      {
        labelId: 'confirmEffort.estimatedEndDate',
        defaultLabel: 'Tahmini Bitiş:',
        value: effortDetails?.estimatedEndDate ? renderFormattedDate(effortDetails.estimatedEndDate) : '',
      },
    ],
    [effortDetails]
  );

  // sync helper functions
  const navigateToIdeconPage = useCallback(() => {
    enqueueSnackbar(<FormattedMessage id="general.invalidLink" defaultMessage="Geçersiz link..!" />, {
      variant: 'warning',
    });

    setTimeout(() => {
      window.location.href = 'https://www.idecon.com.tr/';
    }, 3000);
  }, [enqueueSnackbar]);

  // async helper functions
  const getEffortDetails = useCallback(async () => {
    if (!guid || !isGuid(guid)) return;

    try {
      setLoading(true);

      const response = await apiService.getEffortDetailsForCustomerConfirmation(guid);

      if (response.isSuccess) {
        setEfforDetails(response.data);
        setCompanyVknForInvoice(response.data.companyVknForInvoice);
      }
    } catch {
      navigateToIdeconPage();
    } finally {
      setLoading(false);
    }
  }, [guid, navigateToIdeconPage]);

  const confirmEffort = async (isApproved: boolean) => {
    if (!guid || !isGuid(guid)) return;

    if (isApproved && isSasNecessary && !sasNo) {
      enqueueSnackbar(<FormattedMessage id="confirmEffort.requiredSasNo" defaultMessage="Sas No girilmelidir..!" />, {
        variant: 'warning',
      });
      return;
    }

    if (isApproved && !companyVknForInvoice) {
      enqueueSnackbar(
        <FormattedMessage
          id="confirmEffort.requiredCompanyForInvoice"
          defaultMessage="Fatura Kesilecek VKN girilmelidir..!"
        />,
        {
          variant: 'warning',
        }
      );
      return;
    }

    if (isApproved && !VKN_REGEX.test(companyVknForInvoice)) {
      enqueueSnackbar(
        <FormattedMessage id="confirmEffort.invalidVknFormat" defaultMessage="VKN formatı geçersizdir..!" />,
        {
          variant: 'warning',
        }
      );
      return;
    }

    const postData: EffortConfirmationPostParams = {
      uniqueKey: guid,
      isApproved,
      sasNo: isApproved && isSasNecessary ? sasNo : '',
      isSasNecessary: isApproved ? isSasNecessary : false,
      invoiceNote: isApproved ? invoiceNote : '',
      companyVknForInvoice: isApproved ? companyVknForInvoice : '',
    };

    try {
      setLoading(true);

      const response = await apiService.confirmEffort(postData);

      if (response.isSuccess) {
        setApprovalMessage(isApproved ? 'positive' : 'negative');
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  // useEffect hooks
  useEffect(() => {
    if (!guid || !isGuid(guid)) {
      navigateToIdeconPage();
    }
  }, [guid, navigateToIdeconPage]);

  useEffect(() => {
    if (guid && isGuid(guid)) {
      getEffortDetails();
    }
  }, [guid, getEffortDetails]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: (theme) => (theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900]),
      }}>
      <Container maxWidth="lg" sx={{ pt: { xs: 1.5, sm: 2 }, pb: { xs: 2, sm: 4 } }} component="main">
        <Paper elevation={2} sx={{ mx: 'auto', maxWidth: 1020, overflow: 'hidden' }}>
          <Paper elevation={0}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                px: { xs: 2, sm: 3 },
                py: 1.5,
              }}>
              <img
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  window.location.href = 'https://www.idecon.com.tr/';
                }}
                width={92}
                src={logoSrc}
                alt="logo"
              />
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                }}>
                <LanguagePicker />
                <ColorModeIconDropdown />
              </Box>
            </Box>
          </Paper>
          <Divider />
          {approvalMessage === 'positive' ? (
            <Box
              sx={{
                padding: { xs: 3, sm: 6 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
              <SentimentSatisfiedAltIcon sx={{ fontSize: 78, color: 'grey', mb: 2 }} />
              <Typography fontSize={18}>
                <FormattedMessage
                  id="confirmEffort.approvedSuccess"
                  defaultMessage="Efor başarıyla onaylandı, en kısa sürede çalışma başlatılacaktır."
                />
              </Typography>
              <Typography fontSize={18}>
                <FormattedMessage id="confirmEffort.thankYou" defaultMessage="Teşekkür ederiz. İyi çalışmalar." />
              </Typography>
            </Box>
          ) : approvalMessage === 'negative' ? (
            <Box
              sx={{
                padding: { xs: 3, sm: 6 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
              <SentimentVeryDissatisfiedIcon sx={{ fontSize: 78, color: 'grey', mb: 2 }} />
              <Typography fontSize={18}>
                <FormattedMessage
                  id="confirmEffort.rejectedInfo"
                  defaultMessage="Efor reddedildi, en kısa sürede tarafınızla iletişime geçilecektir."
                />
              </Typography>
              <Typography fontSize={18}>
                <FormattedMessage id="confirmEffort.thankYou" defaultMessage="Teşekkür ederiz. İyi çalışmalar." />
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  paddingTop: '20px',
                  paddingInline: { md: '80px' },
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                }}>
                <Typography
                  sx={{
                    px: 2,
                    py: 2,
                    textAlign: 'center',
                    overflowWrap: 'anywhere',
                  }}>
                  <FormattedMessage id="confirmEffort.dear" defaultMessage="Sayın" />
                  &nbsp;
                  {`${effortDetails?.customerFullName || ''} (${effortDetails?.customerEmail || ''})`}&#44;&nbsp;
                  <FormattedMessage
                    id="confirmEffort.approvalRequestInfo"
                    defaultMessage="aşağıda detayları sunulan efor için onayınız gerekmektedir"
                  />
                  &#46;
                </Typography>
              </Box>
              <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 2 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '180px 1fr' },
                    columnGap: { md: 3 },
                    rowGap: 1.5,
                    alignItems: 'center',
                  }}>
                  {rows.map((r) => (
                    <Box key={r.labelId} sx={{ display: 'contents' }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: 16,
                          pr: { md: 1 },
                          textAlign: { xs: 'left', md: 'right' },
                          whiteSpace: 'nowrap',
                        }}>
                        <FormattedMessage id={r.labelId} defaultMessage={r.defaultLabel} />
                      </Typography>

                      <TextField
                        fullWidth
                        size="small"
                        value={r.value}
                        slotProps={{ htmlInput: { readOnly: true } }}
                        multiline={r.labelId === 'confirmEffort.effortDescription'}
                        minRows={r.labelId === 'confirmEffort.effortDescription' ? 1 : undefined}
                        sx={
                          r.labelId === 'confirmEffort.effortDescription'
                            ? {
                                '& .MuiInputBase-root': {
                                  height: 'auto',
                                },
                              }
                            : {}
                        }
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
              <Divider sx={{ mb: { md: 1.5 } }} />
              <Box sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '180px 1fr 1fr' },
                    columnGap: 3,
                    rowGap: 2,
                    alignItems: 'center',
                  }}>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    labelPlacement="end"
                    label={<FormattedMessage id="confirmEffort.isSasRequired" defaultMessage="SAS zorunlu mu?" />}
                    control={<Switch checked={isSasNecessary} onChange={(e) => setIsSasNecessary(e.target.checked)} />}
                  />
                  <TextField
                    label={<FormattedMessage id="confirmEffort.sasNo" defaultMessage="SAS No" />}
                    fullWidth
                    size="small"
                    disabled={!isSasNecessary}
                    value={isSasNecessary ? sasNo : ''}
                    onChange={(e) => setSasNo(e.target.value)}
                  />
                  <TextField
                    label={
                      <FormattedMessage id="confirmEffort.companyVknForInvoice" defaultMessage="Fatura Kesilecek VKN" />
                    }
                    fullWidth
                    size="small"
                    value={companyVknForInvoice}
                    onChange={(e) => setCompanyVknForInvoice(e.target.value)}
                  />
                </Box>
                <Box sx={{ display: { xs: 'none', md: 'block' } }} />
                <TextField
                  sx={{
                    mt: 2,
                    '& .MuiInputBase-root': {
                      height: 'auto',
                    },
                  }}
                  label={<FormattedMessage id="confirmEffort.invoiceNote" defaultMessage="Fatura Notu" />}
                  fullWidth
                  size="small"
                  multiline
                  minRows={1}
                  value={invoiceNote}
                  onChange={(e) => setInvoiceNote(e.target.value)}
                />
              </Box>
              <Box
                sx={{
                  px: { xs: 2, sm: 3 },
                  pb: 2,
                  display: 'flex',
                  justifyContent: { xs: 'stretch', sm: 'flex-end' },
                  gap: 2,
                  flexDirection: { xs: 'column', sm: 'row' },
                }}>
                <Button
                  fullWidth
                  disabled={loading}
                  sx={{ width: { sm: 190 }, height: 46 }}
                  onClick={() => confirmEffort(false)}
                  variant="contained"
                  color="error">
                  <FormattedMessage id="confirmEffort.reject" defaultMessage="Reddet" />
                </Button>
                <Button
                  fullWidth
                  disabled={loading}
                  sx={{
                    width: { sm: 190 },
                    height: 46,
                    color: 'white',
                    border: 'none',
                    '&:hover': { boxShadow: 'none' },
                  }}
                  onClick={() => confirmEffort(true)}
                  variant="contained"
                  color="success">
                  <FormattedMessage id="confirmEffort.approve" defaultMessage="Onayla" />
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Container>
      <Backdrop
        sx={{ display: 'flex', flexDirection: 'column', color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={!effortDetails || loading}>
        <img src={loaderLogoSrc} alt="loader-logo" />
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default ConfirmEffort;
